import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import {
  CreditCard, ExternalLink, ShieldCheck, Plug, CheckCircle2, XCircle,
  Loader2, Lock, Zap, AlertTriangle, HelpCircle,
} from "lucide-react";

const WIPAY_SIGNUP = "https://www.wipaycaribbean.com/";
const WIPAY_LINKS = {
  apply: "https://jm.wipayfinancial.com/Sign_Up_Business",
  jamaica: "https://wipaycaribbean.com/jamaica",
  help: "https://wipaycaribbean.com/help/faqs",
  support: "https://wipaycaribbean.com/support",
};

export default function WiPaySettings() {
  const [cfg, setCfg] = useState(null);
  const [golive, setGolive] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [testing, setTesting] = useState(false);
  const [form, setForm] = useState({ environment: "sandbox", account_number: "1234567890", api_key: "123", currency: "JMD" });

  const load = () => { api.get("/wipay/config").then((r) => setCfg(r.data)); api.get("/wipay/golive").then((r) => setGolive(r.data)).catch(() => {}); };
  useEffect(() => { load(); }, []);
  if (!cfg) return <p className="text-muted-foreground">Loading…</p>;

  const platformOff = !cfg.platform_available;

  const save = async () => {
    try {
      await api.put("/wipay/config", form);
      toast.success("WiPay credentials saved securely (encrypted)");
      setShowForm(false); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const test = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/wipay/test");
      toast.success(data.message || "Connection OK");
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setTesting(false); }
  };
  const toggleEnable = async (enable) => {
    try { await api.post("/wipay/enable", { enable }); load(); toast.success(enable ? "WiPay enabled at checkout" : "WiPay disabled"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const disconnect = async () => {
    if (!window.confirm("Disconnect WiPay? Cashiers will no longer be able to take WiPay payments.")) return;
    await api.delete("/wipay/config"); load(); toast.success("WiPay disconnected");
  };

  return (
    <div className="space-y-6 max-w-3xl">
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-2xl bg-blue-600 flex items-center justify-center text-white font-head font-extrabold">Wi</div>
        <div>
          <h1 className="font-head text-3xl font-extrabold">WiPay Jamaica</h1>
          <p className="text-muted-foreground">Accept card &amp; WiPay payments, settled directly to your own WiPay merchant account.</p>
        </div>
      </div>

      {platformOff && (
        <Card className="p-4 border-amber-500/40 bg-amber-500/10">
          <p className="text-sm font-semibold text-amber-600 dark:text-amber-400 flex items-center gap-2"><Lock className="w-4 h-4" /> WiPay is currently disabled platform-wide by NEEC.</p>
        </Card>
      )}

      {/* Not connected — first-time WiPay onboarding choices */}
      {!cfg.connected && !showForm && (
        <Card className="p-6" data-testid="wipay-onboarding">
          <p className="font-head font-bold mb-1">Set up WiPay <span className="text-muted-foreground font-normal">(optional)</span></p>
          <p className="text-sm text-muted-foreground mb-5">
            WiPay lets you accept card payments settled directly to <b>your own</b> WiPay merchant account. It's completely
            optional. You can keep running NEEC POS with cash, bank transfer and your other enabled payment methods. NEEC
            never collects your WiPay KYC documents; you verify directly with WiPay.
          </p>
          <div className="grid sm:grid-cols-2 gap-3">
            <Card className="p-5 flex flex-col" data-testid="wipay-have-account">
              <Plug className="w-6 h-6 text-emerald-500" />
              <p className="font-semibold mt-3">Already have a WiPay merchant account?</p>
              <p className="text-xs text-muted-foreground mt-1 flex-1">Enter your WiPay credentials, configure the connection and run verification. You'll be marked <b>CONNECTED</b> only after a successful test.</p>
              <Button onClick={() => setShowForm(true)} disabled={platformOff} className="mt-4 bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="wipay-connect-existing">
                <Plug className="w-4 h-4 mr-1.5" /> Connect My WiPay
              </Button>
            </Card>
            <Card className="p-5 flex flex-col" data-testid="wipay-no-account">
              <ExternalLink className="w-6 h-6 text-blue-500" />
              <p className="font-semibold mt-3">Don't have a WiPay merchant account?</p>
              <p className="text-xs text-muted-foreground mt-1 flex-1">You'll need to obtain your own WiPay merchant account directly from WiPay. Once approved, come back here and choose <b>Connect My WiPay</b>.</p>
              <a href={WIPAY_SIGNUP} target="_blank" rel="noopener noreferrer" className="mt-4" data-testid="wipay-signup">
                <Button variant="outline" className="w-full border-blue-500/40 text-blue-600 dark:text-blue-400">
                  <ExternalLink className="w-4 h-4 mr-1.5" /> Get WiPay / Apply for WiPay
                </Button>
              </a>
              <button type="button" onClick={() => setHelpOpen(true)} className="mt-2 text-xs text-blue-600 dark:text-blue-400 hover:underline inline-flex items-center gap-1 self-start" data-testid="wipay-help-link">
                <HelpCircle className="w-3.5 h-3.5" /> WiPay Setup Help
              </button>
            </Card>
          </div>
        </Card>
      )}

      {/* Credentials form */}
      {!cfg.connected && showForm && (
        <Card className="p-6">
          <p className="font-head font-bold mb-1">Connect WiPay Account</p>
          <p className="text-sm text-muted-foreground mb-4 flex items-center gap-1.5"><ShieldCheck className="w-4 h-4 text-emerald-500" /> Your API key is encrypted at rest and never shown to staff or NEEC.</p>
          <div className="grid sm:grid-cols-2 gap-3">
            <div><Label className="text-xs">Environment</Label>
              <select value={form.environment} onChange={(e) => setForm({ ...form, environment: e.target.value })} className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm" data-testid="wipay-environment">
                <option value="sandbox">Sandbox (test first)</option>
                <option value="live">Live</option>
              </select></div>
            <div><Label className="text-xs">Currency</Label><Input value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })} data-testid="wipay-currency" /></div>
            <div><Label className="text-xs">WiPay Account Number (Merchant ID)</Label><Input value={form.account_number} onChange={(e) => setForm({ ...form, account_number: e.target.value })} data-testid="wipay-account" /></div>
            <div><Label className="text-xs">WiPay API Key</Label><Input type="password" value={form.api_key} onChange={(e) => setForm({ ...form, api_key: e.target.value })} data-testid="wipay-apikey" /></div>
          </div>
          {form.environment === "sandbox" && <p className="text-xs text-muted-foreground mt-2">Sandbox defaults prefilled (account 1234567890 · key 123).</p>}
          <div className="flex gap-2 mt-4">
            <Button onClick={save} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="wipay-save">Save &amp; Connect</Button>
            <Button variant="ghost" onClick={() => setShowForm(false)}>Cancel</Button>
          </div>
        </Card>
      )}

      {/* Connected status */}
      {cfg.connected && (
        <>
          <Card className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <p className="font-head font-bold text-lg">WiPay Jamaica</p>
                  {cfg.verified
                    ? <Badge className="bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border-emerald-500/30" data-testid="wipay-status-badge"><CheckCircle2 className="w-3 h-3 mr-1" /> CONNECTED</Badge>
                    : <Badge className="bg-amber-500/15 text-amber-600 dark:text-amber-400 border-amber-500/30" data-testid="wipay-status-badge"><AlertTriangle className="w-3 h-3 mr-1" /> PENDING VERIFICATION</Badge>}
                  {cfg.enabled
                    ? <Badge className="bg-emerald-500/15 text-emerald-600 border-emerald-500/30">Live at checkout</Badge>
                    : <Badge className="bg-slate-500/15 text-slate-500 border-slate-500/30">Not enabled</Badge>}
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-8 gap-y-2 mt-4 text-sm">
                  <Field label="Merchant" value={cfg.merchant} />
                  <Field label="Currency" value={cfg.currency} />
                  <Field label="Mode" value={cfg.mode} accent={cfg.mode === "LIVE" ? "text-rose-500" : "text-amber-500"} />
                </div>
              </div>
            </div>
            <div className="mt-5 flex flex-wrap items-center gap-2">
              <Button onClick={test} variant="outline" disabled={testing} data-testid="wipay-test">
                {testing ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Zap className="w-4 h-4 mr-1.5" />} Test Connection
              </Button>
              <div className="flex items-center gap-2 px-3 py-2 rounded-lg border border-border" data-testid="wipay-enable-row">
                <span className="text-sm text-muted-foreground">Enable at checkout</span>
                <Switch checked={cfg.enabled} onCheckedChange={toggleEnable} disabled={!cfg.sandbox_tested} data-testid="wipay-enable-toggle" />
              </div>
              <Button onClick={disconnect} variant="outline" className="text-rose-500 border-rose-500/30" data-testid="wipay-disconnect"><XCircle className="w-4 h-4 mr-1.5" /> Disconnect</Button>
            </div>
            {!cfg.verified && (
              <p className="text-xs text-amber-600 dark:text-amber-400 mt-3 flex items-center gap-1.5" data-testid="wipay-verify-prompt"><AlertTriangle className="w-4 h-4" /> Credentials saved. Run a successful <b>Test Connection</b> to verify. Your account shows <b>CONNECTED</b> only after verification passes.</p>
            )}
          </Card>

          <Card className="p-5">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm font-semibold">Go-Live Checklist</p>
              <Badge className={cfg.mode === "LIVE" ? "bg-rose-500/15 text-rose-500 border-rose-500/30" : "bg-amber-500/15 text-amber-600 dark:text-amber-400 border-amber-500/30"} data-testid="wipay-mode-badge">{cfg.mode} MODE</Badge>
            </div>
            {golive && (
              <div className="space-y-1.5">
                {[["credentials_saved", "WiPay credentials saved"], ["currency_set", "Currency configured"], ["callback_configured", "Secure callback URL configured"], ["sandbox_verified", "Sandbox test verified"], ["connection_test_passed", "Latest connection test passed"]].map(([k, lbl]) => (
                  <div key={k} className="flex items-center gap-2 text-sm" data-testid={`golive-${k}`}>
                    {golive.checks[k] ? <CheckCircle2 className="w-4 h-4 text-emerald-500" /> : <XCircle className="w-4 h-4 text-rose-500" />}
                    <span className={golive.checks[k] ? "" : "text-muted-foreground"}>{lbl}</span>
                  </div>
                ))}
                <div className={`mt-3 rounded-lg p-2.5 text-sm font-semibold ${golive.can_go_live ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400" : "bg-amber-500/10 text-amber-600 dark:text-amber-400"}`} data-testid="golive-status">
                  {golive.can_go_live ? "✓ Ready to activate LIVE processing" : "Complete all checks before LIVE processing can be activated"}
                </div>
              </div>
            )}
          </Card>

          <Card className="p-5">
            <p className="text-sm font-semibold flex items-center gap-1.5 mb-2"><ShieldCheck className="w-4 h-4 text-emerald-500" /> How NEEC keeps this safe</p>
            <ul className="text-xs text-muted-foreground space-y-1 list-disc pl-5">
              <li>Your WiPay API key is encrypted at rest and never displayed to cashiers, managers or NEEC administrators.</li>
              <li>Every payment result is verified on our server (signed MD5 hash) before a sale is marked PAID. A browser "success" alone is never trusted.</li>
              <li>The WiPay transaction reference is stored with each sale for reconciliation, refunds and audit.</li>
              <li>Funds settle directly to <b>your</b> WiPay merchant account. NEEC never holds your customers' payments.</li>
            </ul>
          </Card>
        </>
      )}

      <Dialog open={helpOpen} onOpenChange={setHelpOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="wipay-help-modal">
          <DialogHeader>
            <DialogTitle className="font-head flex items-center gap-2"><HelpCircle className="w-5 h-5 text-blue-500" /> WiPay Setup Help</DialogTitle>
            <DialogDescription>How to get a WiPay merchant account and connect it to NEEC POS.</DialogDescription>
          </DialogHeader>
          <ol className="text-sm text-foreground space-y-2 list-decimal pl-5" data-testid="wipay-help-steps">
            <li>Apply for a WiPay merchant / business account.</li>
            <li>Complete verification directly with WiPay.</li>
            <li>Complete WiPay's required banking setup.</li>
            <li>Obtain the credentials needed to connect WiPay to NEEC POS.</li>
            <li>Return to NEEC POS → WiPay Setup → <b>Connect My WiPay</b>.</li>
            <li>Run <b>Test Connection</b>.</li>
            <li>Enable WiPay at Checkout once successfully connected.</li>
          </ol>
          <div className="mt-2 grid gap-2">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Official WiPay Jamaica resources</p>
            <a href={WIPAY_LINKS.apply} target="_blank" rel="noopener noreferrer" data-testid="wipay-help-apply">
              <Button variant="outline" className="w-full justify-start"><ExternalLink className="w-4 h-4 mr-2" /> Apply for a WiPay merchant account</Button>
            </a>
            <a href={WIPAY_LINKS.jamaica} target="_blank" rel="noopener noreferrer" data-testid="wipay-help-jamaica">
              <Button variant="outline" className="w-full justify-start"><ExternalLink className="w-4 h-4 mr-2" /> WiPay Jamaica overview &amp; setup</Button>
            </a>
            <a href={WIPAY_LINKS.help} target="_blank" rel="noopener noreferrer" data-testid="wipay-help-docs">
              <Button variant="outline" className="w-full justify-start"><ExternalLink className="w-4 h-4 mr-2" /> WiPay help &amp; FAQs</Button>
            </a>
            <a href={WIPAY_LINKS.support} target="_blank" rel="noopener noreferrer" data-testid="wipay-help-support">
              <Button variant="outline" className="w-full justify-start"><ExternalLink className="w-4 h-4 mr-2" /> Contact WiPay support</Button>
            </a>
          </div>
          <p className="text-xs text-muted-foreground mt-2">You verify and provide all documents directly to WiPay. NEEC POS never collects your WiPay KYC. WiPay is optional; NEEC POS keeps working with cash, bank transfer and your other enabled methods.</p>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Field = ({ label, value, accent }) => (
  <div><p className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</p><p className={`mono font-bold ${accent || ""}`}>{value}</p></div>
);
